import { useCallback } from "react";

import { useTranslation } from "react-i18next";

import { useAssignCsvEnrichmentToCustomEventMutation } from "@/pages/Private/redux/customEvent/customEvent.api";

import { useSnackBar } from "@/utils/useSnackbar";

import { deliveryErrorKey } from "./scrapeDelivery.errors";

/**
 * Assigning existing files to a source, with an answer either way (PREC-3974).
 *
 * The table used to `.unwrap()` this mutation with nothing to catch the rejection, so a failed
 * assignment threw into the void: the dialog stayed open, the row did not change, and the admin
 * had the same "nothing happens" symptom the delivery cell next to it was reported for. It also
 * closed the dialog before knowing whether the assignment had succeeded.
 *
 * Extracted from `CustomEventTable` so the outcome handling has a spec of its own — the table is
 * a 450-line render tree that no test reaches.
 */
export const useAssignCsvToCustomEvent = () => {
	const { t } = useTranslation();

	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const [assign, { isLoading }] = useAssignCsvEnrichmentToCustomEventMutation();

	/** Resolves to whether the files were assigned, so the caller only closes its dialog if so. */
	const assignCsvEnrichments = useCallback(
		async (customEventId: number, csvEnrichmentIds: number[]): Promise<boolean> => {
			try {
				await assign({ customEventId, csvEnrichmentIds }).unwrap();
				handleOpenSnackbar(t("customEvent.rescrape.admin.filesAssigned"), 4000);

				return true;
			} catch (err) {
				console.error(err);
				handleOpenSnackbar(t(`customEvent.rescrape.admin.errors.${deliveryErrorKey(err)}`), 6000);

				return false;
			}
		},
		[assign, handleOpenSnackbar, t]
	);

	return { assignCsvEnrichments, isLoading, renderMessageSnackbar };
};
